import React, { useRef, useState, useCallback, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, MapPin } from 'lucide-react';
import { GlowCard } from './spotlight-card';

// Arenas with original photos (priority)
import arenamv from '../../assets/images/arenamv.webp';
import xclone from '../../assets/images/xclone.webp';
import vila from '../../assets/images/vila.webp';
import renascenca from '../../assets/images/renascenca.webp';
import lagoa from '../../assets/images/lagoa.webp';
import replay from '../../assets/images/replay.webp';
import sport from '../../assets/images/sport.webp';
import recreativo from '../../assets/images/recreativo.webp';
// Varied generic images for arenas without own photos
import racket from '../../assets/images/racket.webp';
import mainBanner from '../../assets/images/main-banner.webp';
import caraiva from '../../assets/images/caraiva.webp';
import sagradaBeach from '../../assets/images/sagrada-beach.webp';
import dunaSports from '../../assets/images/duna-sports.webp';
import penaareia from '../../assets/images/penaareia.webp';
import g2 from '../../assets/images/g2.webp';

const arenas = [
  // Arenas with original photos first
  { title: 'Arena MV', city: 'Perdões - MG', url: '?state=mg&city=perdoes&arena=Arena MV', img: arenamv },
  { title: 'Lagoa Beach', city: 'Belo Horizonte - MG', url: '?state=mg&city=belohorizonte&arena=Lagoa Beach', img: lagoa },
  { title: 'XClone', city: 'Sete Lagoas - MG', url: '?state=mg&city=setelagoas&arena=XClone', img: xclone },
  { title: 'Villammare', city: 'Belo Horizonte - MG', url: '?state=mg&city=belohorizonte&arena=Villammare', img: vila },
  { title: 'Renascença', city: 'Belo Horizonte - MG', url: '?state=mg&city=belohorizonte&arena=Renascenca', img: renascenca },
  { title: 'Replay', city: 'Belo Horizonte - MG', url: '?state=mg&city=belohorizonte&arena=Replay', img: replay },
  { title: 'Sport Beach BH', city: 'Belo Horizonte - MG', url: '?state=mg&city=belohorizonte&arena=Sport Beach BH', img: sport },
  { title: 'Recreativo', city: 'Belo Horizonte - MG', url: '?state=mg&city=belohorizonte&arena=Recreativo', img: recreativo },
  // Arenas with varied generic images
  { title: 'Arena 240', city: 'Belo Horizonte - MG', url: '?state=mg&city=belohorizonte&arena=Arena 240', img: caraiva },
  { title: 'Arena 2K', city: 'Belo Horizonte - MG', url: '?state=mg&city=belohorizonte&arena=Arena 2K', img: sagradaBeach },
  { title: 'Arena Santa Amélia', city: 'Belo Horizonte - MG', url: '?state=mg&city=belohorizonte&arena=Arena Santa Amélia', img: penaareia },
  { title: 'Cesg Quadra do Nelinho', city: 'Belo Horizonte - MG', url: '?state=mg&city=belohorizonte&arena=Cesg Quadra do Nelinho', img: dunaSports },
  { title: 'Dom Silvério', city: 'Belo Horizonte - MG', url: '?state=mg&city=belohorizonte&arena=Dom Silverio', img: g2 },
  { title: 'Bate Bola', city: 'Sete Lagoas - MG', url: '?state=mg&city=setelagoas&arena=Bate Bola', img: racket },
  { title: 'Prime', city: 'Itamarandiba - MG', url: '?state=mg&city=itamarandiba&arena=Prime', img: mainBanner },
  { title: 'AABB Jequié', city: 'Jequié - BA', url: '?state=ba&city=jequie&arena=AABB Jequie', img: sagradaBeach },
];

export default function ArenaCarousel() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [isAtStart, setIsAtStart] = useState(true);
  const [isAtEnd, setIsAtEnd] = useState(false);

  const checkScroll = useCallback(() => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setIsAtStart(scrollLeft < 10);
      setIsAtEnd(scrollWidth - scrollLeft - clientWidth < 10);
    }
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) {
      el.addEventListener('scroll', checkScroll);
      checkScroll();
    }
    return () => el?.removeEventListener('scroll', checkScroll);
  }, [checkScroll]);

  const scroll = (dir: 'left' | 'right') => {
    if (scrollRef.current) {
      const amount = scrollRef.current.clientWidth * 0.7;
      scrollRef.current.scrollBy({ left: dir === 'right' ? amount : -amount, behavior: 'smooth' });
    }
  };

  return (
    <section className="py-16 md:py-24" style={{ backgroundColor: '#0a0a0a' }}>
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="flex items-end justify-between mb-10">
          <div>
            <span className="inline-block text-orange-400 text-sm font-bold uppercase tracking-widest mb-3">
              Nossas arenas
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-white tracking-tight">
              Onde estamos
            </h2>
            <p className="text-zinc-500 mt-2">Inúmeras arenas gravando em alta qualidade</p>
          </div>
          <div className="hidden md:flex gap-2">
            <button
              onClick={() => scroll('left')}
              disabled={isAtStart}
              className="h-10 w-10 rounded-full border border-zinc-700 bg-zinc-900 flex items-center justify-center text-white hover:border-orange-500 transition-colors disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              onClick={() => scroll('right')}
              disabled={isAtEnd}
              className="h-10 w-10 rounded-full border border-zinc-700 bg-zinc-900 flex items-center justify-center text-white hover:border-orange-500 transition-colors disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Carousel */}
        <div className="relative">
          <div
            ref={scrollRef}
            className="flex gap-4 overflow-x-auto pb-4"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {arenas.map((arena) => (
              <a
                key={arena.title}
                href={`/videos${arena.url}`}
                className="no-underline flex-shrink-0"
              >
                <GlowCard glowColor="orange" className="w-[240px] md:w-[280px]">
                  <motion.div
                    whileHover={{ y: -4 }}
                    transition={{ type: 'spring', stiffness: 300 }}
                  >
                    <div className="relative">
                      <img
                        src={arena.img}
                        alt={arena.title}
                        className="w-full h-[160px] object-cover rounded-t-2xl"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent rounded-t-2xl" />
                    </div>
                    <div className="p-4">
                      <h3 className="text-white font-semibold text-base">{arena.title}</h3>
                      <div className="flex items-center gap-1.5 mt-1.5">
                        <MapPin className="h-3.5 w-3.5 text-orange-400" />
                        <span className="text-zinc-500 text-xs">{arena.city}</span>
                      </div>
                    </div>
                  </motion.div>
                </GlowCard>
              </a>
            ))}
          </div>

          {/* Fade edges */}
          {!isAtStart && (
            <div className="pointer-events-none absolute inset-y-0 left-0 w-16 bg-gradient-to-r from-[#0a0a0a]" />
          )}
          {!isAtEnd && (
            <div className="pointer-events-none absolute inset-y-0 right-0 w-16 bg-gradient-to-l from-[#0a0a0a]" />
          )}
        </div>
      </div>
    </section>
  );
}
